import classNames from 'classnames'
import { useEffect } from 'react'

import useDeviceInfo, { DEVICE_TYPES } from '@src/quince-site-common/src/hooks/useDeviceInfo'
import { useTrackIsElementVisible } from '@src/quince-site-common/src/hooks/useTrackIsElementVisible'
import { IFilterOption } from '@src/quince-site-pdp/src/organisms/productPageSections/productReviews/types'
import { ProductProperties } from '@src/telemetry/getProductPropertiesForTracking'
import { TELEMETRY_EVENT_NAMES } from '@src/telemetry/telemetry-events.constants'
import { trackEvent } from '@src/telemetry/track'

import Swiper, { NAV_POSITION } from '../../custom-swiper'

import styles from './reviewFilterTags.module.scss'

interface ReviewFilterTagsProps {
  filterOptions: IFilterOption[]
  setFilterBy: (filterBy: IFilterOption) => void
  filterBy: IFilterOption | null
  productProperties?: ProductProperties
  setPage: (page: number) => void
  swiperWrapperClass?: string
}

export function ReviewFilterTags({
  filterOptions,
  setFilterBy,
  filterBy,
  productProperties,
  setPage,
  swiperWrapperClass,
}: ReviewFilterTagsProps) {
  const { deviceType } = useDeviceInfo()
  const isMobile = deviceType === DEVICE_TYPES.MOBILE
  const getIsVisibleTrackParams = () => {
    return {
      action: 'pdp_review_tags',
      value: true,
      context: {
        tags_displayed: filterOptions.map(option => `${option.displayName} (${option.count})`).join(','),
        prior_tags_selected: filterBy?.displayName,
        matched_reviews: filterBy?.count,
        productProperties,
      },
    }
  }
  const { ref: trackIsVisibleRef } = useTrackIsElementVisible(getIsVisibleTrackParams)

  useEffect(() => {
    if (filterOptions.length > 0 && filterOptions[0]) {
      setFilterBy(filterOptions[0])
    }
  }, [filterOptions])

  return (
    <div ref={trackIsVisibleRef} className={classNames(styles.reviewFilterTagsContainer, isMobile && styles.fullWidth)}>
      <Swiper
        showDots={false}
        showNavigator={false}
        numberOfVisibleSlides={9}
        wrapperClass={swiperWrapperClass}
        variableWidth
        id="review-filter-tags"
        navPosition={NAV_POSITION.CENTER}
        containerClassName={styles.swiperContainer}
      >
        {filterOptions.map((option, index) => {
          const formattedOptionName = `${option.displayName} ${option.value === 'All' ? '' : `(${option.count})`}`
          return (
            <div
              key={option.value}
              className={classNames(
                styles.reviewFilterTag,
                (filterBy?.value === 'All' && index === 0) || filterBy?.value === option.value ? styles.active : '',
              )}
              onClick={() => {
                setFilterBy(option)
                setPage(1)
                trackEvent(TELEMETRY_EVENT_NAMES.FILTER_APPLIED, {
                  context: {
                    filter_value: `Tag - ${formattedOptionName}`,
                    contentContainerDisplayName: 'Product Rating & Reviews',
                    contentContainerName: 'Sort and Filter Options',
                    contentContainerType: 'Filter',
                    productProperties,
                  },
                })
                trackEvent(TELEMETRY_EVENT_NAMES.BUTTON_CLICK, {
                  action: 'click_review_tags',
                  context: {
                    selection_type: 'Select',
                    tag_clicked: formattedOptionName,
                    tag_position: index + 1,
                    matched_reviews: option.count,
                    prior_tags_selected: filterBy?.displayName,
                    productProperties,
                  },
                })
              }}
            >
              {formattedOptionName}
            </div>
          )
        })}
      </Swiper>
    </div>
  )
}
